import { SkywardException, reportException } from "./skyward.base.error";
import { lazy } from "./skyward.base.extensions";
import { JQueryInput, ValidationData, IframeMethods, HtmlValues, HtmlGathererSettings, Dictionary } from "./types";
import { CancellationError } from "./skyward.base.error";

type ReplyValue<T> = { value: T; } | { error: Error; };

type ClientMethods = IframeMethods & {
    ping(): Promise<void>;
    setupSvgs(svgs: string): Promise<void>;
    iFrameCanPostMessage(): Promise<void>;
};

type SyncFunction<T> = T extends (...args: infer TArgs) => Promise<infer TResult> ?  (...args: TArgs) => (TResult | Promise<TResult>) : never;

type Reply<T extends keyof ClientMethods> = ReturnType<ClientMethods[T]>;
type Args<T extends keyof ClientMethods> = Parameters<ClientMethods[T]>;

type ClientMessage<T extends keyof ClientMethods> = {
    type: T;
    reply: MessagePort;
    data: Args<T>;
};

type ServerMessage = { nonce: string; } &
    ({
        type: "log";
        data: string;
    } | {
        type: "ajax";
        data: AjaxData;
    });

type AjaxData = {
    type: "ajax";
    method: string;
    url: string;
    timeout: number | undefined;
    responseType: XMLHttpRequestResponseType;
    headers: Dictionary<string, string>;
    mimeType: string;
    bodyType: string;
    body: any;
    onComplete(statusCode: number, data: string | ArrayBuffer, headers: string): void;
    onError(): void;
    onUploadProgress(lengthComputable: boolean, loaded: number, total: number): void;
    abort: Promise<void>;
}

type UrlTest = (url: string) => boolean;

type RemotePort = {
    type: "function" | "promise",
    port: MessagePort;
};

type RemotePromiseMessage =
    { success: true; value: any; } |
    { success: false; error: Error; }

enum Statuses
{
    Complete,
    Error,
    Aborted,
}

window.skyward.base.registerScript("iframeListener", function (skyward, $)
{
    const parentEventHandlerIsSetup = $.Deferred();
    const iFrameCanPostMessagePromise = $.Deferred();
    const imagesAreLoadedPromise = Promise.withResolvers<void>();

    const translateToRemote = function (value: any, transfers: Transferable[])
    {
        // Don't do any deep translation
        // It shouldn't be needed for anything we're doing and introduces a risk of misuse
        if (typeof value === "function")
        {
            const callbackChannel = new MessageChannel();
            callbackChannel.port1.onmessage = function (evt)
            {
                value.apply(undefined, evt.data);
            };
            transfers.push(callbackChannel.port2);
            return { type: "function", port: callbackChannel.port2 };
        }
        else if (value instanceof Promise)
        {
            const resolutionChannel = new MessageChannel();
            value.then(
                (value) => resolutionChannel.port1.postMessage({ success: true, value }),
                (error) => resolutionChannel.port1.postMessage({ success: false, error }),
            );
            transfers.push(resolutionChannel.port2);
            return { type: "promise", port: resolutionChannel.port2 };
        }
        else if (value instanceof ArrayBuffer)
        {
            transfers.push(value);
        }
        return value;
    };

    const serverShouldForwardUrl = (iframe: JQuery, url: string) =>
    {
        return (iframe.data("allowedEndpoints") as string).split(",").some(endpoint => allowedUrls[endpoint]!(url));
    };

    const clientShouldForwardUrl = (url: string) =>
    {
        return window._skyward.global.allowedEndpoints.some(endpoint => allowedUrls[endpoint]!(url));
    };

    const UrlTest =
    {
        create(module: string, object: string, screen: string): UrlTest
        {
            const baseLocation = window._skyward.global.host ?? `https?://${window.location.host}${window.skyward.global.virtualDir}`;
            const replaceWildcard = (value: string) => !value || value === "*" ? "[^/]*" : value;
            const pattern = `${baseLocation}${replaceWildcard(module)}/${replaceWildcard(object)}/${replaceWildcard(screen)}(?:\\?.*)?$`;
            const regex = new RegExp(pattern, "i");
            const test = (url: string) => regex.test(url);
            test.pattern = regex;
            return test;
        },

        any(tests: UrlTest[]): UrlTest
        {
            return (url: string) => tests.some(test => test(url));
        }
    };

    const allowedUrls: Dictionary<string, UrlTest> =
    {
        "CodeEEL": UrlTest.any([
            UrlTest.create("*", "*", "PerformCodeEELSearch"),
            UrlTest.create("*", "*", "CodeEEL/\\d+"),
            UrlTest.create("*", "*", "ChangeBrowsePage"),
            UrlTest.create("*", "*", "GetRecordCount"),
            UrlTest.create("SkySys", "Browse", "MonitorBrowsePerformance"),
        ]),
        "Upload": UrlTest.any([
            UrlTest.create("Security", "SessionFileUpload", "(?:Create|Delete)SessionFileUpload"),
            UrlTest.create("SkySys", "Media", "Download\\w+/\\d+"),
        ]),
        "UserForm": UrlTest.create("Form", "FormField", "ReplaceRelationshipDisplay"),
        "UserFormRelationship": UrlTest.any([
            UrlTest.create("Form", "UserForm", "MapRelationship"),
            UrlTest.create("Form", "UserForm", "SubmitSetRelationship"),
            UrlTest.create("Form", "UserForm", "MapRelationshipStudentServices"),
            UrlTest.create("Form", "UserForm", "SubmitSetRelationshipStudentServices")
        ]),
        "UserFormSpecification": UrlTest.create("Form", "FormField", "GetFormFieldSpecification"),
        "SaveFormFieldVersionFromSpecifications": UrlTest.create("Form", "FormFieldVersion", "SaveFormFieldVersionFromSpecifications"),
        "OnlineForm": UrlTest.create("SkySys", "Media", "DownloadMedia/\\d+"),
    };

    const isRemotePort = function (value: any): value is RemotePort
    {
        return typeof value === "object" && (value as RemotePort).port instanceof MessagePort;
    };

    const translateFromRemote = function (value: any)
    {
        if (isRemotePort(value))
        {
            if (value.type === "function")
            {
                return function (...args: any[])
                {
                    const transfers: Transferable[] = [];
                    for (const arg of args)
                    {
                        if (arg instanceof ArrayBuffer)
                        {
                            transfers.push(arg);
                        }
                    }
                    value.port.postMessage(args, transfers);
                };
            }
            else
            {
                return new Promise((res, rej) =>
                {
                    value.port.onmessage = function (event: MessageEvent)
                    {
                        const data = event.data as RemotePromiseMessage;
                        if (data.success)
                        {
                            res(data.value);
                        }
                        else
                        {
                            rej(data.error);
                        }
                    };
                });
            }
        }
        return value;
    };

    const getContentWindowError = function (iframe: HTMLIFrameElement)
    {
        const error = new Error("Iframe does not have a content window");
        (error as any).details =
        {
            connected: iframe.isConnected,
            hasSrc: Boolean(iframe.src),
            hasSrcdoc: Boolean(iframe.srcdoc),
        };
        return error;
    };

    if (window === window.parent)
    {
        skyward.iframeListener = window.skyward.iframeListener || {};

        const emulatedIframes: WeakMap<HTMLIFrameElement, MessagePort> = new WeakMap();
        const readyIframes: WeakMap<HTMLIFrameElement, Promise<HTMLIFrameElement>> = new WeakMap();
        const navigatedIframes: WeakMap<HTMLIFrameElement, Promise<never>> = new WeakMap();

        const sendInternal = function <T extends keyof ClientMethods>(target: HTMLIFrameElement, type: T, data: Args<T>): Promise<Reply<T>>
        {
            const promise: Promise<Reply<T>> = new Promise((resolve, reject) =>
            {
                if (!target.contentWindow)
                {
                    readyIframes.delete(target);
                    emulatedIframes.delete(target);
                    console.warn("called method on detached iframe?");
                    reject(getContentWindowError(target));
                    return;
                }
                const channel = new MessageChannel();
                const transfers = [channel.port2];
                const wrappedArgs = (data as any[]).map(arg => translateToRemote(arg, transfers)) as Args<T>;
                const message: ClientMessage<T> = { type, data: wrappedArgs, reply: channel.port2 };
                const emulatedPort = emulatedIframes.get(target);
                if (emulatedPort)
                {
                    emulatedPort.postMessage(message, transfers);
                }
                else
                {
                    target.contentWindow!.postMessage(message, "*", transfers);
                }
                channel.port1.onmessage = function (evt)
                {
                    const data: ReplyValue<Reply<T>> = evt.data;
                    if (isSuccess(data))
                    {
                        resolve(translateFromRemote(data.value));
                    }
                    else
                    {
                        reject(data.error);
                    }
                };
            });
            return Promise.race([promise, navigatedIframes.get(target)!]);
        };

        const send = function <T extends keyof ClientMethods>(target: JQueryInput, type: T, ...data: Args<T>): Promise<Reply<T>>
        {
            return getIframe(target).then(elem => sendInternal(elem, type, data));
        };

        const getIframe = function (target: JQueryInput): Promise<HTMLIFrameElement>
        {
            const elem = $(target)[0];
            if (!(elem instanceof HTMLIFrameElement))
            {
                return Promise.reject(new Error("Not an iframe"));
            }
            return ready(elem);
        };

        const ready = function (target: HTMLIFrameElement): Promise<HTMLIFrameElement>
        {
            const existingPromise = readyIframes.get(target);
            if (existingPromise)
            {
                return existingPromise;
            }

            if (target.src?.length > 0 && new URL(target.src, window.location.href).origin != new URL(window.location.href).origin)
            {
                return Promise.reject(new Error("Iframe is from a different domain"));
            }
            let isEmulated = false;

            if (target.sandbox && !target.sandbox.contains("allow-scripts"))
            {
                isEmulated = true;
                if (!target.sandbox.contains("allow-same-origin"))
                {
                    return Promise.reject(new Error("No way to communicate with iframe"));
                }
            }

            let observer: MutationObserver;
            const navigatedPromise = new Promise<never>((_, navigated) =>
            {
                // The mutationobserver must be added before any awaits, to ensure we observe
                // navigations performed during this microtask, after returning from this function
                observer = new MutationObserver(() =>
                {
                    navigated(new CancellationError("iframe src changed"));
                });
                observer.observe(target,
                    {
                        attributeFilter: ["src", "srcdoc"],
                        attributes: true,
                    });
            });
            const removedPromise = getDisconnectedPromise()(target).then(() => Promise.reject(new CancellationError("iframe removed from DOM")));
            const combinedPromise = Promise.race([navigatedPromise, removedPromise])
            .finally(() =>
            {
                readyIframes.delete(target);
                emulatedIframes.delete(target);
                observer.disconnect();
            });
            combinedPromise.catch(() => { });
            navigatedIframes.set(target, combinedPromise);

            const loadPromise = $(target).data("loadPromise") ?? Promise.resolve();
            const readyPromise = loadPromise.then(() =>
            {
                let readyInterval: NodeJS.Timer | null = null;

                return new Promise((resolveReady, rejectReady) =>
                {
                    if (!target.contentWindow)
                    {
                        readyIframes.delete(target);
                        rejectReady(getContentWindowError(target));
                        return;
                    }
                    if (isEmulated)
                    {
                        const { port1, port2 } = new MessageChannel();
                        emulatedIframes.set(target, port1);
                        port2.onmessage = buildChildListener(target.contentWindow, true);
                    }

                    let retries = 100;
                    readyInterval = setInterval(() =>
                    {
                        if (retries-- <= 0)
                        {
                            readyIframes.delete(target);
                            rejectReady(new Error(`Timed out connecting to iframe "${target.id}"`));
                            return;
                        }
                        // note that sendInternal uses Promise.race(navigatedPromise)
                        // this takes care of cases where the iframe src changes before loadPromise resolves
                        sendInternal(target, "ping", []).then(resolveReady).catch(rejectReady);
                    }, 50);
                })
                .then(() =>
                {
                    parentEventHandlerIsSetup.then(() =>
                    {
                        sendInternal(target, "iFrameCanPostMessage", []);
                    });

                    return additionalSetup(target).then(() => target);
                })
                .finally(() =>
                {
                    if (readyInterval !== null)
                    {
                        clearInterval(readyInterval);
                    }
                })
                .catch(() =>
                {
                    // If we fail due to navigation, try again from the top.
                    // Note that this could still fail if the iframe has been removed from the DOM.
                    return ready(target);
                })
            })
            readyIframes.set(target, readyPromise);
            return readyPromise;
        };

        window.addEventListener("message", function (event: MessageEvent)
        {
            const data = event.data as ServerMessage;
            if (!data || !data.type || !data.nonce)
            {
                return;
            }
            const sourceIframe = ($("iframe") as JQuery<HTMLIFrameElement>).filter(function ()
            {
                return this.contentWindow === event.source;
            });
            if (!sourceIframe.length)
            {
                return;
            }
            // only handle messages from html viewers or other local URLs
            // This won't work for emulated iframes, but we only expect to getState replies from them anyway
            if (!sourceIframe[0].hasAttribute("srcdoc") && new URL(sourceIframe.attr("src")!, window.location.href).host !== window.location.host)
            {
                console.log("message received from external iframe");
                return;
            }
            // if the iframe has navigated internally, the attribute won't change but it should no longer have access to the nonce
            if (sourceIframe.data("messageNonce") !== data.nonce)
            {
                reportException(new SkywardException("message received from iframe with incorrect/missing nonce"));
                return;
            }
            switch (data.type)
            {
                case "log":
                    reportException(new SkywardException(data.data as string));
                    break;
                case "ajax":
                    const ajaxData = data.data;
                    if (!serverShouldForwardUrl(sourceIframe, ajaxData.url))
                    {
                        throw new Error(`iframe attempting to forward ajax request to url not in allowed list: ${ajaxData.url}`);
                    }
                    ajaxData.onComplete = translateFromRemote(ajaxData.onComplete);
                    ajaxData.onError = translateFromRemote(ajaxData.onError);
                    ajaxData.onUploadProgress = translateFromRemote(ajaxData.onUploadProgress);
                    ajaxData.abort = translateFromRemote(ajaxData.abort);
                    sendAjax(ajaxData);
                    break;
            }
        });

        parentEventHandlerIsSetup.resolve();
        const sendAjax = function (data: AjaxData)
        {
            var xhr = new XMLHttpRequest();
            xhr.open(data.method, data.url);
            if (data.timeout)
            {
                xhr.timeout = data.timeout;
            }
            for (let name in data.headers)
            {
                xhr.setRequestHeader(name, data.headers[name]!);
            }
            if (data.mimeType)
            {
                xhr.overrideMimeType(data.mimeType);
            }
            xhr.responseType = data.responseType;
            xhr.onabort = xhr.onerror = xhr.ontimeout = function ()
            {
                data.onError();
            };
            xhr.onload = function ()
            {
                data.onComplete(xhr.status, xhr.response, xhr.getAllResponseHeaders());
            }
            xhr.upload.onprogress = function (event)
            {
                data.onUploadProgress(event.lengthComputable, event.loaded, event.total);
            }
            data.abort.then(() =>
            {
                xhr.abort();
            });
            let body = data.body;
            if (data.bodyType === "FormData")
            {
                const form = new FormData();
                for (let [key, values] of Object.entries(data.body as Dictionary<string, any[]>))
                {
                    for (let value of values!)
                    {
                        if (value instanceof File)
                        {
                            // As of 10-2024 Chrome doesn't like uploading files transferred from frames, so launder it into a new file
                            const clonedFile = new File([value], value.name,
                                {
                                    lastModified: value.lastModified,
                                    type: value.type,
                                });
                            form.append(key, clonedFile);
                        }
                        else
                        {
                            form.append(key, value);
                        }
                    }
                }
                body = form;
            }
            xhr.send(body);
        };

        const isSuccess = function <T>(reply: ReplyValue<T>): reply is { value: T; }
        {
            return typeof (reply as any).error === "undefined";
        };

        const additionalSetup = async function (target: HTMLIFrameElement)
        {
            const spritesheet = await $.ajax({
                url: `${skyward.global.virtualDir}skysys/svg/spritesheet?v=2"`,
                dataType: "text",
                cache: true,
            });
            await sendInternal(target, "setupSvgs", [spritesheet]);
        };

        skyward.iframeListener.ready = function (iframe: JQueryInput)
        {
            return getIframe(iframe).then(function () { return undefined; });
        };

        const methods = Object.keys(buildClientMethods(window)) as (keyof ClientMethods)[];
        for (const key of methods)
        {
            (skyward.iframeListener as any)[key] = function (iframe: JQueryInput, ...args: any[])
            {
                return send(iframe, key, ...args as any);
            };
        }
        skyward.iframeListener.connect = function (iframe: JQueryInput)
        {
            return getIframe(iframe).then(iframe =>
            {
                const originalNavigation = navigatedIframes.get(iframe)!;
                const result: Partial<ClientMethods> = {};
                for (const key of methods)
                {
                    result[key] = function (...args: any[])
                    {
                        return Promise.race([originalNavigation, sendInternal(iframe, key, args as any)]);
                    } as any;
                }
                return result as ClientMethods;
            });
        }
    }
    else
    {
        window.addEventListener("message", buildChildListener(window, false));
        addScriptedIframeHelpers();
    }

    function buildClientMethods(window: Window): ClientMethods
    {
        // We can't usefully shadow the global document because window.document can change if the document isn't loaded yet.
        // Instead, this ensures any accidental uses will fail early.
        // See https://stackoverflow.com/a/24156526/9118790.
        // @ts-ignore: Unused variable
        const document = null;
        const $ = window.jQuery;

        const methods: { [key in keyof ClientMethods]: SyncFunction<ClientMethods[key]> } =
        {
            getHeight(): number
            {
                const doc = window.document.documentElement;
                let height = $(doc).outerHeight(true)!;

                var datepickers = $('.datepicker:not([readonly])');
                if (datepickers.length > 0)
                {
                    // initializing the datepicker widget now also prevents IE and Edge from adding a scrollbar when the widget tries to measure its
                    // height for the first time
                    height = Math.max(height, window.skyward.datepicker.getMaxDatepickerHeight(window));
                }
                height += skyward.window.scrollbarHeight;

                return height;
            },

            setupElements()
            {
                $('textarea').attr('style', 'resize: none; overflow: auto;');
            },

            setupChangeTracking(listener: (dirty: boolean) => void)
            {
                $('input,select,textarea').each(function ()
                {
                    var control = $(this);
                    control.attr('data-original-value', control.val() as any);
                });
                window.skyward.changeTracking.setUpChangeTracking(window.document.documentElement, () =>
                {
                    listener(window.skyward.changeTracking.isDirty(window.document.documentElement));
                });
            },

            setDocumentOverflow(overflow: string)
            {
                // Assume the worst case for space consumed by scrollbars: we're tall enough for our scrollParent to need a vertical scrollbar
                // (shrinking our width and causing our content to wrap earlier) and wide enough to need a horizontal scrollbar. If all goes
                // well, we won't need a vertical scrollbar inside the iframe.
                $(window.document.documentElement).css('overflow-y', overflow);
            },

            preSave()
            {
                $('input,select,textarea').removeAttr('data-original-value');
            },

            getHtml()
            {
                return skyward.htmlgatherer.getHtml(window.document.documentElement);
            },

            getFieldValues()
            {
                return skyward.dataobjectviewer.getFieldValues($(window.document.documentElement));
            },

            displayValidationMessages(data: ValidationData)
            {
                return skyward.validation.displayValidationMessages(data, $(window.document.documentElement));
            },

            clearDirtyList()
            {
                return skyward.changeTracking.clearDirtyList(undefined, window);
            },

            getBoundingClientRect()
            {
                return window.document.body.getBoundingClientRect();
            },

            getScrollHeight()
            {
                return window.document.body.scrollHeight;
            },

            waitForLoadedAndNonEmpty()
            {
                return new Promise(resolve =>
                {
                    checkReady();
                    function checkReady()
                    {
                        if (window.document.readyState !== 'complete')
                        {
                            $(window.document).one('load', checkReady);
                            return;
                        }

                        // Fix for Safari which may have an empty body but still reports a readyState of 'complete'
                        // (originally added in PR 4775223)
                        var iFrameMissingContents = window.document.body.children.length === 0;
                        if (iFrameMissingContents)
                        {
                            setTimeout(checkReady, 100);
                            return;
                        }

                        resolve();
                    }
                });
            },

            getHtmlValues()
            {
                return skyward.htmlgatherer.getValues($(window.document) as JQuery<any>);
            },

            setHtmlValues(values: HtmlValues)
            {
                return skyward.htmlgatherer.setValues(values, $(window.document) as JQuery<any>);
            },

            async getOuterHtml(options: HtmlGathererSettings)
            {
                var html = await skyward.htmlgatherer.getNode($(window.document.documentElement), options);
                var outerHtmlValue = skyward.htmlgatherer.getOuterHtml(html);
                return outerHtmlValue;
            },

            setupSvgs(svgText: string)
            {
                const temp = window.document.createElement("div");
                temp.innerHTML = svgText;
                window.document.head.appendChild(temp.firstChild!);
                for (const elem of Array.from(window.document.getElementsByTagName("use")))
                {
                    const xlinkNS = "http://www.w3.org/1999/xlink";
                    var oldLocation = elem.getAttributeNS(xlinkNS, "href")!;
                    var fragment = /#svg-.*$/.exec(oldLocation)?.[0];
                    if (fragment)
                    {
                        elem.setAttributeNS(xlinkNS, "href", fragment);
                    }
                }
            },

            initForm()
            {
                return skyward.form.initInIframe();
            },

            async ping(): Promise<void>
            {
                // Iframes can report "uninitialized" when checked early enough.
                // They don't fire load events from this state and it seems to be equivalent to "complete"
                // See https://bugzilla.mozilla.org/show_bug.cgi?id=1191683 
                if (window.document.readyState !== "complete" && (window.document.readyState as string) !== "uninitialized")
                {
                    await new Promise(resolve => $(window.document).one("load", resolve));
                }
                return new Promise(resolve => $(resolve));
            },

            iFrameCanPostMessage(): Promise<void>
            {
                iFrameCanPostMessagePromise.resolve();
                return Promise.resolve();
            },

            imagesAreLoaded(): Promise<void>
            {
                return imagesAreLoadedPromise.promise;
            }
        }

        return Object.assign(Object.create(null), methods);
    }

    function buildChildListener(window: Window, trustMessages: boolean)
    {
        if (!window.jQuery)
        {
            const wrappedJQuery: typeof jQuery = $.extend(function (this: any, selector: any)
            {
                if (typeof selector === "string" && arguments.length === 1)
                {
                    return ($ as any).call(this, selector, window.document);
                }
                else
                {
                    return $.apply(this, arguments as any);
                }
            }, $);
            wrappedJQuery.prototype = $.extend({}, $.prototype);
            (window as any).$ = window.jQuery = wrappedJQuery;
            window.skyward = skyward;
        }

        const methods = buildClientMethods(window);
        return function (event: MessageEvent)
        {
            if (!trustMessages && (event.source !== window.parent || !event.isTrusted))
            {
                return;
            }
            let data = event.data as ClientMessage<keyof ClientMethods>;
            if (!data || !data.type || !data.data || !data.reply)
            {
                return;
            }
            try
            {
                const unwrappedArgs = (data.data as any[]).map(value => translateFromRemote(value));
                const method = methods[data.type] as Function | undefined;
                if (!method)
                {
                    throw new Error(`Unknown type ${data.type}`);
                }
                const result = (methods[data.type] as (...args: any) => any)?.(...unwrappedArgs);
                Promise.resolve(result).then(resolvedResult =>
                {
                    const transfers: MessagePort[] = [];
                    const value = translateToRemote(resolvedResult, transfers);
                    data.reply.postMessage({ value }, transfers);
                });
            }
            catch (error)
            {
                console.log(`replying to iframe message ${data.type} with error`, error);
                try
                {
                    data.reply.postMessage({ error });
                }
                catch (_e)
                {
                    data.reply.postMessage({ error: error?.message ?? String(error) });
                }
            }
        };
    }

    function addScriptedIframeHelpers()
    {
        window.addEventListener("unhandledrejection", function (event)
        {
            postError(event.reason);
        });

        window.addEventListener("error", function (event)
        {
            postError(event.error,
                {
                    filename: event.filename,
                    lineno: event.lineno,
                    colno: event.colno,
                });
        });

        const postError = function (rawError: any, extraData?: object)
        {
            const error = rawError instanceof Error ? rawError : String(rawError);
            const errorData =
            {
                error,
                ...extraData ?? {},
            };
            try
            {
                window._skyward.global.postMessage({ type: "log", data: errorData });
            }
            // Errors themselves are serializable but anything could be attached to them
            catch (_e)
            {
                errorData.error = `Error in iframe: ${(error as Error).message ?? error}`;
                window._skyward.global.postMessage({ type: "log", data: errorData });
            }
        };

        class IframeParentXHR extends XMLHttpRequest
        {
            constructor()
            {
                super();
                this._unsupported = [];
                this._headers = {};
                this._forwarded = false;
            }
            _requestUrl: string | undefined;
            _method: string | undefined;
            _unsupported: string[];
            _status: Statuses | undefined;
            _statusCode: number | undefined;
            _response: any;
            _rawResponse: string | ArrayBuffer | Blob | object | Document | undefined;
            _responseText: string | undefined;
            _responseXml: XMLDocument | undefined;
            _responseHeaders: string | undefined;
            _headers: Dictionary<string, string>;
            _forwarded: boolean;
            _abort: (() => void) | undefined;
            _mimeType: string | undefined;

            get status()
            {
                if (!this._forwarded) { return super.status; }
                switch (this._status)
                {
                    case Statuses.Complete:
                        return this._statusCode!;
                    case Statuses.Error:
                    case Statuses.Aborted:
                        return 400;
                    default:
                        return 0;
                }
            }

            get statusText()
            {
                if (!this._forwarded) { return super.statusText; }
                switch (this._status)
                {
                    case Statuses.Complete:
                        return "OK";
                    case Statuses.Error:
                        return "Error";
                    case Statuses.Aborted:
                        return "Aborted";
                    default:
                        return "";
                }
            }

            get readyState()
            {
                if (!this._forwarded) { return super.readyState; }
                return this._status !== undefined ? 4 : 0;
            }

            get response()
            {
                if (!this._forwarded) { return super.response; }
                if (this._rawResponse !== undefined && this._response === undefined)
                {
                    switch (this.responseType)
                    {
                        case "arraybuffer":
                        case "blob":
                        case "json":
                        case "text":
                            this._response = this._rawResponse;
                            break;
                        case "document":
                            this._response = new DOMParser().parseFromString(this.responseText, "text/html");
                            break;
                        default:
                            throw new Error(`unimplemented response type: ${this.responseType}`);
                    }
                }
                return this._response;
            }

            get responseXML()
            {
                if (!this._forwarded) { return super.responseXML; }
                if (this._responseText !== undefined && this._responseXml === undefined)
                {
                    this._responseXml = new DOMParser().parseFromString(this._responseText!, "text/xml");
                }
                return this._responseXml ?? null;
            }

            get responseText()
            {
                if (!this._forwarded) { return super.responseText; }
                if (typeof this._responseText !== "string")
                {
                    if (this._rawResponse instanceof Blob || this._rawResponse instanceof ArrayBuffer || this._rawResponse instanceof Document)
                    {
                        // we can read blobs using Blob.text() or FileReader but not synchronously, arraybuffers are doable but have to be copied by hand
                        // hopefully if you requested one of those response types it's because you didn't want text
                        throw new Error("responseText is not supported for responseType blob or arraybuffer");
                    }
                    else if (typeof this._rawResponse === "object")
                    {
                        this._responseText = JSON.stringify(this._rawResponse);
                    }
                    else
                    {
                        this._responseText = this._rawResponse;
                    }
                }
                return this._responseText ?? "";
            }

            setRequestHeader(key: string, value: string): void
            {
                if (!this._forwarded) { return super.setRequestHeader(key, value); }
                if (key !== "X-CSRF-TOKEN" && key !== "X-Requested-With" && key !== "Accept" && key !== "Content-Type")
                {
                    this._unsupported.push(`custom headers: "${key}"`);
                    return;
                }
                this._headers[key] = value;
            }

            getAllResponseHeaders()
            {
                if (!this._forwarded) { return super.getAllResponseHeaders(); }
                return this._responseHeaders ?? "";
            }

            getResponseHeader(name: string)
            {
                if (!this._forwarded) { return super.getResponseHeader(name); }
                const headers = (this._responseHeaders ?? "").trim().split(/\r?\n/);
                const keySearch = name.toLowerCase() + ": ";
                return headers.find(h => h.startsWith(keySearch))?.slice(keySearch.length) ?? null;
            }

            addEventListener<K extends keyof XMLHttpRequestEventMap>(type: K, listener: (this: XMLHttpRequest, ev: XMLHttpRequestEventMap[K]) => any, options?: boolean | AddEventListenerOptions): void
            {
                if (type !== "load" && type !== "abort" && type !== "error" && type !== "loadend")
                {
                    this._unsupported.push(`unusual event listeners: "${type}"`);
                }
                super.addEventListener(type, listener, options);
            }

            open(method: string, url: string, async?: boolean, username?: string | null, password?: string | null): void
            {
                if (!async)
                {
                    this._unsupported.push("sync");
                }
                if (username || password)
                {
                    this._unsupported.push("credentials");
                }
                this._method = method;

                let absoluteUrl = url;
                try
                {
                    absoluteUrl = new URL(url, window._skyward.global.host).href;
                } catch { }
                this._requestUrl = absoluteUrl;
                if (clientShouldForwardUrl(absoluteUrl))
                {
                    this._forwarded = true;
                }
                else
                {
                    super.open(method, url, async ?? false, username, password);
                }
            }

            send(body?: Document | XMLHttpRequestBodyInit | null)
            {
                if (this._unsupported.length > 0)
                {
                    console.warn(`AJAX request in iframe uses unsupported features: ${this._unsupported.join(", ")}`);
                }
                if (!this._forwarded) { return super.send(body); }

                const oncomplete = (statusCode: number, data: string | ArrayBuffer, headers: string) =>
                {
                    this._status = Statuses.Complete;
                    this._statusCode = statusCode;
                    this._rawResponse = data;
                    this._responseHeaders = headers;
                    this.dispatchEvent(new ProgressEvent("load"));
                    this.dispatchEvent(new ProgressEvent("loadend"));
                };

                const onerror = () =>
                {
                    this._status = Statuses.Error;
                    this.dispatchEvent(new ProgressEvent("error"));
                    this.dispatchEvent(new ProgressEvent("loadend"));
                };
                const onUploadProgress = (lengthComputable: boolean, loaded: number, total: number) =>
                {
                    this.upload.dispatchEvent(new ProgressEvent("progress",
                        {
                            lengthComputable,
                            loaded,
                            total,
                        }));
                };

                const abort = new Promise<void>((res) =>
                {
                    this._abort = res;
                });

                let newBody: any = undefined;
                let bodyType: string = "";
                const transfer: Transferable[] = [];

                if (typeof body !== "string" && body !== null && body !== undefined && !(body instanceof FormData) && !((window as any).ReadableStream && body instanceof ReadableStream) &&
                    !(body instanceof Blob) && !(body instanceof ArrayBuffer))
                {
                    throw new Error("Unsupported body type");
                }

                if (body instanceof ArrayBuffer)
                {
                    transfer.push(body as any);
                }

                if (body instanceof FormData)
                {
                    newBody = {};
                    bodyType = "FormData";
                    body.forEach((value: FormDataEntryValue, key) =>
                    {
                        newBody[key] = newBody[key] ?? [];
                        newBody[key].push(value);
                    });
                }

                window._skyward.global.postMessage(
                    {
                        type: "ajax",
                        data:
                        {
                            method: this._method,
                            url: this._requestUrl,
                            timeout: this.timeout,
                            responseType: this.responseType === "document" ? "text" : this.responseType,
                            mimeType: this._mimeType,
                            bodyType: bodyType,
                            body: newBody ?? body,
                            headers: this._headers,
                            onComplete: translateToRemote(oncomplete, transfer),
                            onError: translateToRemote(onerror, transfer),
                            onUploadProgress: translateToRemote(onUploadProgress, transfer),
                            abort: translateToRemote(abort, transfer),
                        }
                    }, transfer);
            }

            abort()
            {
                if (!this._forwarded) { super.abort(); }
                this._abort?.();
            }

            overrideMimeType(type: string)
            {
                if (!this._forwarded) { super.overrideMimeType(type); }
                this._mimeType = type;
            }
        }

        window.jQuery.ajaxSettings.xhr = () => new IframeParentXHR();

        skyward.window.registerLoadDOMCallback(async function ()
        {
            await iFrameCanPostMessagePromise;
            const images = this.find(`img:is([src^="${window.skyward.global.virtualDir}SkySys/Media/"], [src^="${window.skyward.global.virtualDir}SkySys/Media/DownloadMedia/"])`) as JQuery<HTMLImageElement>;
            const loadPromises = images.toArray().map(async img =>
            {
                const data = await $.ajax(
                {
                    url: img.src,
                    method: "GET",
                    xhr: () =>
                    {
                        const xhr = new IframeParentXHR();
                        xhr.responseType = "blob";
                        return xhr;
                    }
                });
                const url = URL.createObjectURL(data);
                const imgLoaded = new Promise(res =>
                {
                    img.onload = res;
                    img.onerror = res; // we could add an empty catch() instead but it's the same either way
                });
                img.src = url;
                await imgLoaded;
            });
            await Promise.all(loadPromises);
            imagesAreLoadedPromise.resolve();
        });
        skyward.window.registerLoadDOMCallback(function ()
        {
            this.find("svg use").each(function ()
            {
                const xlinkNS = "http://www.w3.org/1999/xlink";
                const svgId = /#svg-\w+/.exec(this.getAttributeNS(xlinkNS, "href")!)![0];
                this.setAttributeNS(xlinkNS, "href", svgId);
            });
        });
    }

    const getDisconnectedPromise = lazy(function ()
    {
        if (window.CustomElementRegistry)
        {
            class DisconnectWatcher extends HTMLElement
            {
                disconnectedPromise: Promise<void>;
                private _res!: () => void;
                constructor()
                {
                    super();
                    this.disconnectedPromise = new Promise(res =>
                    {
                        this._res = res;
                    });
                }
                disconnectedCallback()
                {
                    this._res();
                    this.remove();
                }
            }
            window.customElements.define("disconnect-watcher", DisconnectWatcher);
            const cache = skyward.extensions.createCache((element: HTMLElement) =>
            {
                const watcher = new DisconnectWatcher();
                element.appendChild(watcher);
                return watcher.disconnectedPromise.finally(() =>
                {
                    cache.remove(element);
                });
            });
            return cache;
        }
        else
        {
            const cache = skyward.extensions.createCache((element: HTMLElement) =>
            {
                return new Promise<void>(res =>
                {
                    $(element).one("DOMNodeRemovedFromDocument", () =>
                    {
                        cache.remove(element);
                        res();
                    });
                });
            });
            return cache;
        }
    });
});